import type { RehostChangeDTO } from '@devrobot/shared';
import { ArrowRight, TriangleAlert } from 'lucide-react';
import { ConfirmDialog } from './ConfirmDialog';

/** Confirms a VPS domain or hostname-template change that moves generated hostnames. */
export function RehostConfirmDialog({ title, changes, busy, error, onConfirm, onCancel }: { title: string; changes: RehostChangeDTO[]; busy?: boolean; error?: unknown; onConfirm: () => void; onCancel: () => void }) {
  const running = changes.filter((c) => c.deployed).length;
  return (
    <ConfirmDialog title={title} confirmLabel={`Move ${changes.length} hostname${changes.length === 1 ? '' : 's'}`} tone="warning" size="lg" busy={busy} error={error} onConfirm={onConfirm} onCancel={onCancel}>
      <p className="text-sm">
        These applications use their generated hostname, so it follows the change. Each old URL stops working once its move completes. Hostnames chosen by hand are not changed.
      </p>
      {/* Phones: one stacked row per application, old hostname above the new one. */}
      <ul className="divide-y divide-hairline rounded-field border border-hairline md:hidden">
        {changes.map((c) => (
          <li key={`${c.targetId}:${c.from}`} className="space-y-1 px-3 py-2.5">
            <p className="text-sm font-medium">
              {c.project} / {c.environment}
            </p>
            <p className="text-xs text-base-content/60">
              {c.vps}
              {c.deployed ? '' : ' · not deployed yet'}
            </p>
            <p className="font-mono text-xs break-anywhere text-base-content/60 line-through decoration-base-content/30">{c.from}</p>
            <p className="flex items-start gap-1 font-mono text-xs break-anywhere">
              <ArrowRight className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
              <span>{c.to}</span>
            </p>
          </li>
        ))}
      </ul>
      <div className="overflow-x-auto rounded-field border border-hairline max-md:hidden">
        <table className="table table-sm">
          <thead>
            <tr className="text-xs tracking-wide text-base-content/50 uppercase">
              <th>Application</th>
              <th>Current hostname</th>
              <th>New hostname</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((c) => (
              <tr key={`${c.targetId}:${c.from}`} className="hover:bg-base-200/50">
                <td>
                  <span className="font-medium">
                    {c.project} / {c.environment}
                  </span>
                  <span className="block text-xs text-base-content/60">
                    {c.vps}
                    {c.deployed ? '' : ' · not deployed yet'}
                  </span>
                </td>
                <td className="font-mono text-xs break-anywhere text-base-content/65">{c.from}</td>
                <td className="font-mono text-xs break-anywhere">{c.to}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {running > 0 ? (
        <div role="note" className="alert alert-warning alert-soft text-sm">
          <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
          <span>
            {running} running application{running === 1 ? ' gets' : 's get'} a route-only update job (same release and configuration, no migrations). Update links and bookmarks that use the old URLs.
          </span>
        </div>
      ) : null}
    </ConfirmDialog>
  );
}
